import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import "./index.css";
import { BrandLogo } from "./components/BrandLogo";
import { useSession, signOut } from "./auth/client";
import { Login } from "./pages/Login";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import { Leads } from "./pages/Leads";
import { Settings } from "./pages/Settings";
import { AcceptInvite } from "./pages/AcceptInvite";
import { Dashboard } from "./pages/Dashboard";
import { Closers } from "./pages/Closers";
import { Setters } from "./pages/Setters";
import { Leaderboard } from "./pages/Leaderboard";
import { UTMBuilder } from "./pages/UTMBuilder";
import { RevProjector } from "./pages/RevProjector";
import { EOCReports } from "./pages/EOCReports";
import { EOCForm } from "./pages/public/EOCForm";
import { Ads } from "./pages/Ads";
import { Webinars } from "./pages/Webinars";

import { DateRangeProvider, useDateRange } from "./context/DateRangeContext";
import { FilterProvider, useFilters } from "./context/FilterContext";
import { SyncProvider } from "./context/SyncContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { UserProvider, useUser } from "./context/UserContext";
import { DateRangePicker } from "./components/DateRangePicker";
import { AIChatDialog } from "./components/AIChatDialog";

// Icons as SVG components
const Icons = {
  Dashboard: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  Teams: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  Leaderboard: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Reports: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  Deals: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
    </svg>
  ),
  Commissions: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Quotas: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  CallLibrary: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
  Templates: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  Integrations: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  Tools: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
    </svg>
  ),
  Website: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
    </svg>
  ),
  Ads: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
  Billing: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  Settings: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Bell: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  Search: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  TrendUp: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  TrendDown: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
    </svg>
  ),
  ChevronRight: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  ),
  Logout: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  Support: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Community: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
    </svg>
  ),
  Guide: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  Tour: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  Subscribers: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  Analytics: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  Revenue: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Sun: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Moon: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  ),
};

// Chevron Down Icon for collapsible sections
const ChevronDown = ({ className }: { className?: string }) => (
  <svg className={className || "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// Navigation item types
interface NavItem {
  icon?: React.ReactNode;
  label: string;
  path?: string;
  permission?: string;
  children?: NavItem[];
}

// Collapsible Nav Section Component
function NavSection({
  item,
  isExpanded,
  onToggle,
  location,
  buildSearchString,
  onNavigate,
}: {
  item: NavItem;
  isExpanded: boolean;
  onToggle: () => void;
  location: ReturnType<typeof useLocation>;
  buildSearchString: (targetPath: string) => string;
  onNavigate?: () => void;
}) {
  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren && item.children?.some(
    (child) => location.pathname === child.path
  );

  if (!hasChildren && item.path) {
    // Simple nav item without children
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        to={{ pathname: item.path, search: buildSearchString(item.path) }}
        onClick={onNavigate}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
          isActive
            ? 'bg-gradient-to-r from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 text-[var(--brand-primary)] font-medium'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
      >
        {item.icon}
        <span className="text-sm">{item.label}</span>
      </NavLink>
    );
  }

  // Collapsible section with children
  return (
    <div className="space-y-0.5">
      <button
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
          isChildActive
            ? 'text-[var(--brand-primary)] font-medium'
            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        }`}
      >
        {item.icon}
        <span className="text-sm flex-1">{item.label}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Children */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-4 space-y-0.5">
          {item.children?.map((child) => {
            const isActive = location.pathname === child.path;
            const targetPath = child.path || '/';
            return (
              <NavLink
                key={child.label}
                to={{ pathname: targetPath, search: buildSearchString(targetPath) }}
                onClick={onNavigate}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 text-[var(--brand-primary)] font-medium'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                <span className="text-sm">{child.label}</span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Sidebar Component
function Sidebar({ userName, onLogout: _onLogout, isOpen, onClose }: {
  userName: string;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { formatForApi } = useDateRange();
  const { hasPermission, user } = useUser();
  const {
    dateField,
    closers,
    setters,
    contents,
    platforms,
    coaches,
    funnels,
    countries,
    sources,
    leadTypes,
  } = useFilters();

  // Track expanded sections (Urban Unity navigation)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    "sales analytics": true,
    marketing: true,
    pipeline: true,
    tools: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Define which filters each page supports (all use plural names to match FilterContext)
  const pageFilters: Record<string, string[]> = {
    "/": ["closers", "setters", "contents", "funnels", "countries", "sources", "leadTypes"], // Dashboard
    "/leaderboard": ["closers", "setters", "contents", "platforms", "coaches"],
    "/closers": ["closers", "contents"],
    "/setters": ["setters", "contents"],
    "/content": ["contents"],
    "/acquisition": ["closers", "setters", "contents", "funnels"],
    "/payment-collection": ["closers", "setters"],
    "/leads": ["closers", "setters", "funnels", "countries", "sources"],
    "/calls": ["closers", "setters"],
  };

  // Build search string with only filters relevant to target page
  const buildSearchString = (targetPath: string) => {
    const params = new URLSearchParams();
    const apiDates = formatForApi();

    // Always include date range
    params.set("startDate", apiDates.startDate);
    params.set("endDate", apiDates.endDate);
    if (dateField && dateField !== "dateCallWasTaken") params.set("dateField", dateField);

    // Get supported filters for this page (default to shared filters only)
    const supported = pageFilters[targetPath] || ["closers", "setters", "contents"];

    // Only include filters that the target page supports
    // All filters use comma-separated format (?key=a,b,c) to match NUQS parseAsArrayOf
    if (supported.includes("closers") && closers.length > 0) params.set("closers", closers.join(","));
    if (supported.includes("setters") && setters.length > 0) params.set("setters", setters.join(","));
    if (supported.includes("contents") && contents.length > 0) params.set("contents", contents.join(","));
    if (supported.includes("platforms") && platforms.length > 0) params.set("platforms", platforms.join(","));
    if (supported.includes("coaches") && coaches.length > 0) params.set("coaches", coaches.join(","));
    if (supported.includes("funnels") && funnels.length > 0) params.set("funnels", funnels.join(","));
    if (supported.includes("countries") && countries.length > 0) params.set("countries", countries.join(","));
    if (supported.includes("sources") && sources.length > 0) params.set("sources", sources.join(","));
    if (supported.includes("leadTypes") && leadTypes.length > 0) params.set("leadTypes", leadTypes.join(","));

    return params.toString();
  };

  // The Artillery Navigation Structure
  // Each item has a permission key that maps to the route permission
  const navItems: NavItem[] = [
    // OVERVIEW
    { icon: <Icons.Dashboard />, label: "Dashboard", path: "/", permission: "dashboard" },

    // SALES ANALYTICS
    {
      icon: <Icons.Analytics />,
      label: "Sales Analytics",
      children: [
        { label: "Leaderboard", path: "/leaderboard", permission: "leaderboard" },
        { label: "Closers", path: "/closers", permission: "closers" },
        { label: "Setters", path: "/setters", permission: "setters" },
        { label: "Leads", path: "/leads", permission: "leads" },
      ],
    },

    // MARKETING
    {
      icon: <Icons.Ads />,
      label: "Marketing",
      children: [
        { label: "Ads", path: "/ads", permission: "ads" },
        { label: "Webinars", path: "/webinars", permission: "webinars" },
      ],
    },

    // TOOLS
    {
      icon: <Icons.Tools />,
      label: "Tools",
      children: [
        { label: "UTM Builder", path: "/utm-builder", permission: "utm-builder" },
        { label: "Rev Projector", path: "/rev-projector", permission: "rev-projector" },
        { label: "EOC Reports", path: "/eoc-reports", permission: "admin" },
      ],
    },

  ];

  // Filter nav items based on user permissions
  const filteredNavItems = navItems.map(item => {
    // For items with children, filter the children based on permissions
    if (item.children) {
      const filteredChildren = item.children.filter(child => 
        !child.permission || hasPermission(child.permission)
      );
      // Only include the parent if it has visible children
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    }
    // For items without children, check the permission directly
    if (item.permission && !hasPermission(item.permission)) return null;
    return item;
  }).filter((item): item is NavItem => item !== null);

  // Get initials from name
  const initials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <>
      {/* Backdrop - mobile only */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`
        w-64 bg-card border-r border-border h-screen fixed left-0 top-0 flex flex-col z-50
        transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-border flex justify-center">
          <BrandLogo variant="primary" className="h-10 w-auto" />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const sectionKey = item.label.toLowerCase();
            return (
              <NavSection
                key={item.label}
                item={item}
                isExpanded={expandedSections[sectionKey] ?? false}
                onToggle={() => toggleSection(sectionKey)}
                location={location}
                buildSearchString={buildSearchString}
                onNavigate={onClose}
              />
            );
          })}
        </nav>

        {/* User Profile Section */}
        <div className="p-3 border-t border-border space-y-3">
          {/* User Info - clicks to admin */}
          <div
            className="flex items-center gap-3 p-2 rounded-xl hover:bg-secondary transition-colors cursor-pointer"
            onClick={() => { navigate("/admin"); onClose(); }}
          >
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] flex items-center justify-center text-white text-sm font-semibold">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{userName}</p>
              <p className="text-xs text-muted-foreground truncate capitalize">{user?.role || "Member"}</p>
            </div>
          </div>

          {/* Theme Toggle */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </aside>
    </>
  );
}

// Theme Toggle Component
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-200"
      title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
    >
      {theme === "light" ? <Icons.Moon /> : <Icons.Sun />}
    </button>
  );
}

// Header Component
function Header({ onOpenAI, onToggleSidebar }: { onOpenAI: () => void; onToggleSidebar: () => void }) {
  const { dateRange, setDateRange, formatForDisplay } = useDateRange();
  const [showDatePicker, setShowDatePicker] = useState(false);

  const handleApplyDateRange = (start: Date, end: Date) => {
    setDateRange({ startDate: start, endDate: end });
    setShowDatePicker(false);
  };

  return (
    <header className="h-14 md:h-16 bg-card border-b border-border flex items-center justify-between px-3 md:px-8 sticky top-0 z-30">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        {/* Hamburger menu - mobile only */}
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg hover:bg-secondary md:hidden flex-shrink-0"
          aria-label="Toggle menu"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-base md:text-xl font-semibold truncate">Dashboard</h1>
        <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs md:text-sm font-medium flex-shrink-0">
          Live
        </span>
      </div>

      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        {/* Ask AI Button */}
        <button
          onClick={onOpenAI}
          className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 text-[var(--brand-primary)] font-medium text-sm hover:from-[var(--brand-gradient-start)]/20 hover:to-[var(--brand-gradient-end)]/20 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
          </svg>
          <span className="hidden sm:inline">Ask AI</span>
        </button>

        {/* Date Range Picker */}
        <div className="relative">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-xl bg-secondary text-foreground text-sm hover:bg-secondary/80 transition-all"
          >
            <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            <span className="hidden sm:inline">{formatForDisplay()}</span>
            <svg className="w-4 h-4 text-muted-foreground flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Date Range Picker Dropdown */}
          {showDatePicker && (
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onApply={handleApplyDateRange}
              onClose={() => setShowDatePicker(false)}
            />
          )}
        </div>
      </div>
    </header>
  );
}

// Placeholder component for pages not yet implemented
function ComingSoon({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--brand-gradient-start)]/10 to-[var(--brand-gradient-end)]/10 flex items-center justify-center mb-4">
        <Icons.Settings />
      </div>
      <h2 className="text-2xl font-bold mb-2">{title}</h2>
      <p className="text-muted-foreground">This page is coming soon.</p>
    </div>
  );
}

// Access Denied component
function AccessDenied() {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center">
      <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
      <p className="text-muted-foreground">You don't have permission to view this page.</p>
      <p className="text-sm text-muted-foreground mt-2">Contact your administrator to request access.</p>
    </div>
  );
}

// Protected Route wrapper that checks permissions
function ProtectedRoute({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { hasPermission, loading } = useUser();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (!hasPermission(permission)) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}

// Main Dashboard Layout Component
function DashboardLayout({ userName, onLogout }: { userName: string; onLogout: () => void }) {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        userName={userName}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="md:ml-64">
        <Header
          onOpenAI={() => setAiDialogOpen(true)}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />

        <main className="p-4 md:p-8">
          <Routes>
            {/* OVERVIEW */}
            <Route path="/" element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute permission="leaderboard"><Leaderboard /></ProtectedRoute>} />

            {/* SALES ANALYTICS */}

            <Route path="/closers" element={<ProtectedRoute permission="closers"><Closers /></ProtectedRoute>} />
            <Route path="/setters" element={<ProtectedRoute permission="setters"><Setters /></ProtectedRoute>} />

            {/* MARKETING */}
            <Route path="/ads" element={<ProtectedRoute permission="ads"><Ads /></ProtectedRoute>} />
            <Route path="/webinars" element={<ProtectedRoute permission="webinars"><Webinars /></ProtectedRoute>} />

            {/* PIPELINE */}
            <Route path="/leads" element={<ProtectedRoute permission="leads"><Leads /></ProtectedRoute>} />

            {/* TOOLS */}
            <Route path="/utm-builder" element={<ProtectedRoute permission="utm-builder"><UTMBuilder /></ProtectedRoute>} />
            <Route path="/rev-projector" element={<ProtectedRoute permission="rev-projector"><RevProjector /></ProtectedRoute>} />
            <Route path="/eoc-reports" element={<ProtectedRoute permission="admin"><EOCReports /></ProtectedRoute>} />

            {/* ACCOUNT */}
            <Route path="/admin" element={<ProtectedRoute permission="admin"><Settings /></ProtectedRoute>} />

            {/* Legacy routes - redirect to new paths */}
            <Route path="/deals" element={<Navigate to="/acquisition" replace />} />
            <Route path="/settings" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      {/* AI Chat Dialog */}
      <AIChatDialog open={aiDialogOpen} onOpenChange={setAiDialogOpen} />
    </div>
  );
}

// Loading Spinner
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <BrandLogo variant="primary" className="h-12 w-auto" />
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)]/20 border-t-[var(--brand-primary)] rounded-full animate-spin" />
      </div>
    </div>
  );
}

// Inner App component that uses router hooks
function AppContent() {
  const { data: session, isPending } = useSession();
  const [hasInitialized, setHasInitialized] = useState(false);
  // Keep track of the last known session to prevent flashing login on refetch
  const [cachedSession, setCachedSession] = useState<typeof session>(null);
  const location = useLocation();

  // Track when we've completed initial session check and cache the session
  useEffect(() => {
    if (!isPending) {
      if (!hasInitialized) {
        setHasInitialized(true);
      }
      // Update cached session when we have a definitive answer (not during refetch)
      if (session !== undefined) {
        setCachedSession(session);
      }
    }
  }, [isPending, hasInitialized, session]);

  const handleLogout = async () => {
    setCachedSession(null); // Clear cache on logout
    await signOut();
  };

  // Only show loading on initial load, not on subsequent refetches
  // This prevents the page from flickering when switching browser tabs
  if (isPending && !hasInitialized) {
    return <LoadingScreen />;
  }

  // Handle invite page (accessible without login)
  if (location.pathname === "/invite") {
    return <AcceptInvite />;
  }

  if (location.pathname === "/forgot-password") {
    return <ForgotPassword />;
  }
  if (location.pathname === "/reset-password") {
    return <ResetPassword />;
  }

  // Public EOC form — fully standalone, no sidebar/dashboard
  if (location.pathname === "/eoc") {
    return <EOCForm />;
  }

  // Use cached session during refetch to prevent unmounting dashboard
  // This prevents data reload when switching browser tabs
  const effectiveSession = session ?? cachedSession;

  // Show auth pages if not logged in (and we're sure about it)
  if (!effectiveSession && hasInitialized) {
    return <Login onSuccess={() => {}} />;
  }

  // Still determining session state
  if (!effectiveSession) {
    return <LoadingScreen />;
  }

  // Show dashboard if logged in
  return (
    <DashboardLayout
      userName={effectiveSession.user.name}
      onLogout={handleLogout}
    />
  );
}

// Main App Component with Router
export function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <NuqsAdapter>
          <UserProvider>
            <DateRangeProvider>
              <FilterProvider>
                <SyncProvider>
                  <AppContent />
                </SyncProvider>
              </FilterProvider>
            </DateRangeProvider>
          </UserProvider>
        </NuqsAdapter>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
